import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Pencil, Trash2, Plus, Images, PawPrint, Check, X, ClipboardList, StickyNote, Pin, Copy } from 'lucide-react';

import { customersApi } from '../../../api/customers.js';
import { petsApi } from '../../../api/pets.js';
import { consultationsApi } from '../../../api/consultations.js';
import { staffNotesApi } from '../../../api/staffNotes.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

import { CustomerFormModal } from './CustomerFormModal.jsx';
import { PetFormModal } from './PetFormModal.jsx';
import { PetPhotosModal } from './PetPhotosModal.jsx';
import { ConsultationFormModal } from './ConsultationFormModal.jsx';
import { CustomerLinkModal } from './CustomerLinkModal.jsx';

const SEX_LABEL = { male: 'Maschio', female: 'Femmina', unknown: 'Sesso n.d.' };
const CONSULT_TYPE_LABEL = { general: 'Generale', nutrition: 'Nutrizione', behaviour: 'Comportamento', health: 'Salute', other: 'Altro' };

function fullName(c) {
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Cliente';
}

async function copyValue(value, message) {
  try { await navigator.clipboard.writeText(String(value)); toast.success(message); }
  catch { /* clipboard unavailable */ }
}

function ageLabel(birthDate) {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  const months = Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.4)));
  if (months < 12) return `${months} mesi`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem ? `${years} anni ${rem} mesi` : `${years} anni`;
}

function primaryPhoto(pet) {
  const photos = pet.photos || [];
  return photos.find((p) => p.isPrimary && p.url) || photos.find((p) => p.url) || null;
}

function ConsentPill({ ok, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
      {ok ? <Check className="h-3.5 w-3.5 text-success" /> : <X className="h-3.5 w-3.5 text-ink-faint" />}
      {label}
    </span>
  );
}

export function CustomerDetailPage({ basePath = '/admin/customers' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const canUpdateCustomer = hasPermission('customers:update');
  const canDeleteCustomer = hasPermission('customers:delete');
  const canCreatePet = hasPermission('pets:create');
  const canUpdatePet = hasPermission('pets:update');
  const canDeletePet = hasPermission('pets:delete');
  const canReadConsult = hasPermission('consultations:read');
  const canCreateConsult = hasPermission('consultations:create');
  const canDeleteConsult = hasPermission('consultations:delete');
  const canReadNotes = hasPermission('notes:read');
  const canCreateNotes = hasPermission('notes:create');
  const canDeleteNotes = hasPermission('notes:delete');
  const canLink = hasPermission('customers:link');

  const customerQuery = useQuery({ queryKey: ['customer', id], queryFn: () => customersApi.get(id) });
  const petsQuery = useQuery({ queryKey: ['customer', id, 'pets'], queryFn: () => petsApi.list({ customerId: id, limit: 100 }) });
  const consultationsQuery = useQuery({ queryKey: ['customer', id, 'consultations'], enabled: canReadConsult, queryFn: () => consultationsApi.list({ customerId: id, limit: 50 }) });
  const notesQuery = useQuery({ queryKey: ['customer', id, 'notes'], enabled: canReadNotes, queryFn: () => staffNotesApi.list({ customerId: id, limit: 50 }) });

  const customer = customerQuery.data;
  const pets = (petsQuery.data && petsQuery.data.items) || [];
  const consultations = (consultationsQuery.data && consultationsQuery.data.items) || [];
  const notes = (notesQuery.data && notesQuery.data.items) || [];
  const petName = (pid) => { const p = pets.find((x) => String(x._id) === String(pid?._id || pid)); return p ? p.name : null; };

  const [editCustomer, setEditCustomer] = useState(false);
  const [deleteCustomer, setDeleteCustomer] = useState(false);
  const [petForm, setPetForm] = useState({ open: false, pet: null });
  const [petPhotos, setPetPhotos] = useState({ open: false, pet: null });
  const [deletePet, setDeletePet] = useState(null);
  const [consultForm, setConsultForm] = useState({ open: false, consultation: null });
  const [deleteConsult, setDeleteConsult] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [notePetId, setNotePetId] = useState('');
  const [notePinned, setNotePinned] = useState(false);

  const deleteCustomerMutation = useMutation({
    mutationFn: () => customersApi.remove(id),
    onSuccess: () => { toast.success('Cliente eliminato.'); navigate(basePath); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const deletePetMutation = useMutation({
    mutationFn: (petId) => petsApi.remove(petId),
    onSuccess: () => {
      toast.success('Animale eliminato.');
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'pets'] });
      setDeletePet(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const deleteConsultMutation = useMutation({
    mutationFn: (cid) => consultationsApi.remove(cid),
    onSuccess: () => {
      toast.success('Consulenza eliminata.');
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'consultations'] });
      setDeleteConsult(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const invalidateNotes = () => queryClient.invalidateQueries({ queryKey: ['customer', id, 'notes'] });
  const createNoteMutation = useMutation({
    mutationFn: () => staffNotesApi.create({ customerId: id, petId: notePetId || null, text: noteText.trim(), pinned: notePinned }),
    onSuccess: () => { toast.success('Nota salvata.'); setNoteText(''); setNotePetId(''); setNotePinned(false); invalidateNotes(); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const togglePinMutation = useMutation({
    mutationFn: (note) => staffNotesApi.update(note._id, { pinned: !note.pinned }),
    onSuccess: invalidateNotes,
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const deleteNoteMutation = useMutation({
    mutationFn: (nid) => staffNotesApi.remove(nid),
    onSuccess: () => { toast.success('Nota eliminata.'); invalidateNotes(); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  if (customerQuery.isLoading) {
    return <div className="flex flex-col gap-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;
  }
  if (!customer) {
    return (
      <Card><p className="text-sm text-ink-muted">Cliente non trovato. <button className="underline" onClick={() => navigate(basePath)}>Torna all'elenco</button></p></Card>
    );
  }

  const storeName = customer.storeId && (customer.storeId.name || customer.storeId);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <button type="button" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink" onClick={() => navigate(basePath)}>
          <ArrowLeft className="h-3.5 w-3.5" /> Clienti
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-ink">{fullName(customer)}</h1>
            <p className="text-sm text-ink-muted">
              {[customer.email, customer.phone].filter(Boolean).join(' - ') || 'Nessun contatto'}
            </p>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="text-xs text-ink-faint">ID:</span>
              <code className="rounded bg-surface-subtle px-1.5 py-0.5 text-xs text-ink-muted">{customer._id}</code>
              <button
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(customer._id); toast.success('ID copiato'); }
                  catch { /* clipboard unavailable */ }
                }}
                className="text-ink-faint hover:text-ink"
                title="Copia ID"
                aria-label="Copia ID cliente"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUpdateCustomer && <Button variant="secondary" onClick={() => setEditCustomer(true)}><Pencil className="mr-1.5 h-4 w-4" />Modifica</Button>}
            {canDeleteCustomer && <Button variant="danger" onClick={() => setDeleteCustomer(true)}><Trash2 className="mr-1.5 h-4 w-4" />Elimina</Button>}
          </div>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <p className="text-xs uppercase tracking-wide text-ink-faint">Contatti</p>
            <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              {customer.email
                ? (
                  <span className="inline-flex items-center gap-1.5">
                    <a href={`mailto:${customer.email}`} className="text-ink hover:text-brand hover:underline">{customer.email}</a>
                    <button type="button" onClick={() => copyValue(customer.email, 'Email copiata')} className="text-ink-faint hover:text-ink" title="Copia email" aria-label="Copia email"><Copy className="h-3.5 w-3.5" /></button>
                  </span>
                )
                : <span className="text-ink-faint">Email non disponibile</span>}
              {customer.phone
                ? (
                  <span className="inline-flex items-center gap-1.5">
                    <a href={`tel:${customer.phone}`} className="text-ink hover:text-brand hover:underline">{customer.phone}</a>
                    <button type="button" onClick={() => copyValue(customer.phone, 'Telefono copiato')} className="text-ink-faint hover:text-ink" title="Copia telefono" aria-label="Copia telefono"><Copy className="h-3.5 w-3.5" /></button>
                  </span>
                )
                : <span className="text-ink-faint">Telefono non disponibile</span>}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-faint">Negozio</p>
            <p className="text-sm text-ink">{storeName || '-'}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-faint">Stato</p>
            <Badge tone={customer.status === 'active' ? 'success' : 'neutral'}>{customer.status === 'active' ? 'Attivo' : 'Archiviato'}</Badge>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-faint">Account portale</p>
            <div className="mt-0.5 flex items-center gap-2">
              <Badge tone={customer.userId ? 'success' : 'neutral'}>{customer.userId ? 'Collegato' : 'Non collegato'}</Badge>
              {canLink && !customer.userId && (
                <button type="button" onClick={() => setLinkOpen(true)} className="text-sm text-brand hover:text-brand-hover">
                  Collega
                </button>
              )}
            </div>
          </div>
          <div className="sm:col-span-3">
            <p className="text-xs uppercase tracking-wide text-ink-faint">Consensi</p>
            <div className="mt-1 flex flex-wrap gap-4">
              <ConsentPill ok={Boolean(customer.consent?.dataProcessing?.granted)} label="Trattamento dati" />
              <ConsentPill ok={Boolean(customer.consent?.photos?.granted)} label="Foto animale" />
            </div>
          </div>
          {(customer.tags || []).length > 0 && (
            <div className="sm:col-span-3">
              <p className="text-xs uppercase tracking-wide text-ink-faint">Tag</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {customer.tags.map((tag) => <Badge key={tag} tone="neutral">{tag}</Badge>)}
              </div>
            </div>
          )}
          {customer.notes && (
            <div className="sm:col-span-3">
              <p className="text-xs uppercase tracking-wide text-ink-faint">Note</p>
              <p className="whitespace-pre-wrap text-sm text-ink-muted">{customer.notes}</p>
            </div>
          )}
        </div>
      </Card>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink"><PawPrint className="h-4 w-4" />Animali</h2>
          {canCreatePet && <Button onClick={() => setPetForm({ open: true, pet: null })}><Plus className="mr-1.5 h-4 w-4" />Nuovo animale</Button>}
        </div>

        {petsQuery.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-44 w-full" />)}</div>
        ) : pets.length === 0 ? (
          <Card><p className="text-sm text-ink-muted">Nessun animale registrato per questo cliente.</p></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pets.map((pet) => {
              const photo = primaryPhoto(pet);
              const age = ageLabel(pet.birthDate);
              const species = pet.speciesId && (pet.speciesId.name || '');
              const breed = pet.breedId && (pet.breedId.name || '');
              return (
                <Card key={pet._id} className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-surface">
                      {photo ? <img src={photo.url} alt={pet.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-ink-faint"><PawPrint className="h-6 w-6" /></div>}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{pet.name}</p>
                      <p className="truncate text-xs text-ink-muted">{[species, breed].filter(Boolean).join(' - ') || SEX_LABEL[pet.sex]}</p>
                      {age && <p className="text-xs text-ink-faint">{age}</p>}
                      <button
                        type="button"
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(String(pet._id)); toast.success('ID animale copiato'); }
                          catch { /* clipboard unavailable */ }
                        }}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink"
                        title="Copia ID animale"
                        aria-label={`Copia ID di ${pet.name}`}
                      >
                        <Copy className="h-3 w-3" />
                        <code className="truncate">{String(pet._id).slice(-8)}</code>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canUpdatePet && <Button size="sm" variant="secondary" onClick={() => setPetPhotos({ open: true, pet })}><Images className="mr-1 h-3.5 w-3.5" />Foto{pet.photos?.length ? ` (${pet.photos.length})` : ''}</Button>}
                    {canUpdatePet && <Button size="sm" variant="ghost" onClick={() => setPetForm({ open: true, pet })}>Modifica</Button>}
                    {canDeletePet && <button type="button" className="ml-auto text-xs text-danger hover:underline" onClick={() => setDeletePet(pet)}>Elimina</button>}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {canReadConsult && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-semibold text-ink"><ClipboardList className="h-4 w-4" />Consulenze</h2>
            {canCreateConsult && <Button variant="secondary" onClick={() => setConsultForm({ open: true, consultation: null })}><Plus className="mr-1.5 h-4 w-4" />Registra consulenza</Button>}
          </div>
          {consultations.length === 0 ? (
            <Card><p className="text-sm text-ink-muted">Nessuna consulenza registrata.</p></Card>
          ) : (
            <div className="flex flex-col gap-2">
              {consultations.map((c) => (
                <Card key={c._id} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone="info">{CONSULT_TYPE_LABEL[c.type] || c.type}</Badge>
                      <span className="text-xs text-ink-faint">{c.date ? new Date(c.date).toLocaleDateString('it-IT') : ''}</span>
                      {petName(c.petId) && <span className="text-xs text-ink-muted">{petName(c.petId)}</span>}
                      {c.followUpAt && <Badge tone="warning">follow-up {new Date(c.followUpAt).toLocaleDateString('it-IT')}</Badge>}
                    </div>
                    {c.summary && <p className="mt-1 text-sm text-ink">{c.summary}</p>}
                    {(c.recommendations || []).length > 0 && <p className="text-xs text-ink-muted">Consigli: {c.recommendations.join(', ')}</p>}
                    {(c.products || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {c.products.map((p, i) => (
                          <Badge key={i} tone={p.purchased ? 'success' : 'neutral'}>{p.name}{p.purchased ? ' · acquistato' : ' · consigliato'}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  {canDeleteConsult && <button type="button" className="shrink-0 text-xs text-danger hover:underline" onClick={() => setDeleteConsult(c)}>Elimina</button>}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {canReadNotes && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-ink"><StickyNote className="h-4 w-4" />Note del personale</h2>
          {canCreateNotes && (
            <Card className="mb-3 flex flex-col gap-2">
              <textarea
                rows={2}
                className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-sm focus:outline-none"
                placeholder="Aggiungi un'osservazione su questo cliente..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <select className="h-8 rounded-md border border-border bg-surface-elevated px-2 text-sm" value={notePetId} onChange={(e) => setNotePetId(e.target.value)}>
                    <option value="">Cliente (generale)</option>
                    {pets.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-ink-muted">
                    <input type="checkbox" checked={notePinned} onChange={(e) => setNotePinned(e.target.checked)} /> In evidenza
                  </label>
                </div>
                <Button size="sm" onClick={() => createNoteMutation.mutate()} loading={createNoteMutation.isPending} disabled={!noteText.trim()}>Salva nota</Button>
              </div>
            </Card>
          )}
          {notes.length === 0 ? (
            <Card><p className="text-sm text-ink-muted">Nessuna nota.</p></Card>
          ) : (
            <div className="flex flex-col gap-2">
              {notes.map((n) => (
                <Card key={n._id} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {n.pinned && <Pin className="h-3.5 w-3.5 text-accent" />}
                      {petName(n.petId) && <span className="text-xs text-ink-muted">{petName(n.petId)}</span>}
                      <span className="text-xs text-ink-faint">{n.createdAt ? new Date(n.createdAt).toLocaleDateString('it-IT') : ''}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{n.text}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {canCreateNotes && <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => togglePinMutation.mutate(n)}>{n.pinned ? 'Rimuovi' : 'Evidenzia'}</button>}
                    {canDeleteNotes && <button type="button" className="text-xs text-danger hover:underline" onClick={() => deleteNoteMutation.mutate(n._id)}>Elimina</button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <CustomerFormModal open={editCustomer} onClose={() => { setEditCustomer(false); queryClient.invalidateQueries({ queryKey: ['customer', id] }); }} initial={customer} />
      <PetFormModal open={petForm.open} onClose={() => setPetForm({ open: false, pet: null })} customerId={id} initial={petForm.pet} />
      <PetPhotosModal open={petPhotos.open} onClose={() => setPetPhotos({ open: false, pet: null })} customerId={id} pet={petPhotos.pet ? (pets.find((p) => p._id === petPhotos.pet._id) || petPhotos.pet) : null} />
      <ConsultationFormModal open={consultForm.open} onClose={() => setConsultForm({ open: false, consultation: null })} customerId={id} pets={pets} initial={consultForm.consultation} />
      <CustomerLinkModal open={linkOpen} onClose={() => setLinkOpen(false)} customer={customer} />

      <ConfirmDialog
        open={deleteCustomer}
        title="Eliminare il cliente?"
        description={`"${fullName(customer)}" e tutti i suoi animali e foto saranno rimossi. Operazione irreversibile.`}
        destructive
        confirmLabel="Elimina"
        loading={deleteCustomerMutation.isPending}
        onCancel={() => setDeleteCustomer(false)}
        onConfirm={() => deleteCustomerMutation.mutate()}
      />
      <ConfirmDialog
        open={Boolean(deletePet)}
        title="Eliminare l'animale?"
        description={deletePet ? `"${deletePet.name}" e le sue foto saranno rimossi.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deletePetMutation.isPending}
        onCancel={() => setDeletePet(null)}
        onConfirm={() => deletePet && deletePetMutation.mutate(deletePet._id)}
      />
      <ConfirmDialog
        open={Boolean(deleteConsult)}
        title="Eliminare la consulenza?"
        description={deleteConsult ? 'La consulenza sarà rimossa dalla memoria storica.' : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteConsultMutation.isPending}
        onCancel={() => setDeleteConsult(null)}
        onConfirm={() => deleteConsult && deleteConsultMutation.mutate(deleteConsult._id)}
      />
    </div>
  );
}
